import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { contentPadding, pageHeight, pageWidth } from '../../../constants/gridConfig'
import { useStore } from '../../../store/store'
import { pageBackground, variableHandlingType } from '../../../constants/constants'
import { VList } from 'virtua'
import FormItem from './FormItem'
import { sortAndFilterVariablesGroupedByContainer } from '../../../utils/variableOrderArray'
import { shouldHideRowInForm } from '../../../utils/variables'
import { useQueryDocumentSections } from '../../../hooks'
import ApplyAllPredefinedValues from './ApplyAllPredefinedValues'
import { withAddressParts } from '../../../utils/address'

const selector = ({
  configuration,
  currentDocument
}) => {
  return {
    fontSize: configuration.fontSize,
    fontFamily: configuration.fontFamily,
    documentType: currentDocument.type
  }
}

const listRef = React.createRef();

export const scrollToItemInVariableEditor = (boxVariableSelectedId, formItems) => {
  const containerIdx = formItems.filter((vInfos) => {
    if (vInfos.type === variableHandlingType.FORM) return true
    if (vInfos.variableGroupBox)
      return vInfos.variablesBoxes.some((v) => !v.notUsed)
    return !vInfos.variablesBoxes[0].notUsed
  }).findIndex((v) => {
    if (v.variableGroupBox && v.variableGroupBox.id === boxVariableSelectedId) return true
    return withAddressParts(v.variablesBoxes).some((b) => b.id === boxVariableSelectedId)
  })
  if (containerIdx < 0) return;
  listRef.current?.scrollToIndex(containerIdx);
};

const FormViewer = ({ variablesGroupedByContainer, factor = 30 }) => {
  const { fontSize, fontFamily } = useStore(selector)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const {
    landscape
  } = useStore(selector);
  const documentType = useStore(({ currentDocument }) => currentDocument.type)
  const documentId = useStore(({ currentDocument, currentProposal }) =>
    currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id
  );

  const { data: sectionList } = useQueryDocumentSections({
    documentType,
    documentId,
  });

  const boxVariableSelectedId = useStore(({ boxVariableSelectedId }) => boxVariableSelectedId)
  const variableOrderArray = useStore(({ variableOrderArray }) => variableOrderArray)
  const displayedVariables = useStore(({ displayedVariables }) => displayedVariables)
  const [zoom, setZoom] = useState(100)

  const formItems = useMemo(() => {
    const sortedAndFiltered = sortAndFilterVariablesGroupedByContainer({ variablesGroupedByContainer, variableOrderArray, filterHiddenBoxes: true }).filter((vInfos) => {
      if (shouldHideRowInForm({ vInfos, sectionList })) return false
      return true;
    });

    // Logique de déduplication basique pour le mode proposal
    if (documentType !== 'template') {
      const seenVariableIds = new Set();
      const seenGroupIds = new Set();

      return sortedAndFiltered
        .map((vInfos) => {
          // Pour les groupements
          if (vInfos.variableGroupBox) {
            const groupId = vInfos.variableGroupBox.id;
            // Si ce groupement a déjà été vu, ne pas l'afficher
            if (seenGroupIds.has(groupId)) {
              return null; // Groupement déjà affiché
            }
            // Vérifier si toutes les variables sont déjà dans displayedVariables
            const allVariablesDisplayed = vInfos.variablesBoxes.every((b) => displayedVariables[b.id]);
            if (allVariablesDisplayed) {
              return null; // Toutes les variables déjà affichées
            }
            seenGroupIds.add(groupId);
            // Filtrer les variables cachées ou non utilisées dans le document
            const filteredVariables = vInfos.variablesBoxes.filter((v) => !v.hideField && !v.notUsed);
            return {
              ...vInfos,
              variablesBoxes: filteredVariables
            };
          }

          // Pour les variables individuelles
          const filteredVariables = vInfos.variablesBoxes.filter((v) => {
            if (v.hideField) return false;
            // Déduplication : exclure les variables déjà dans displayedVariables ou déjà vues
            if (displayedVariables[v.id] || seenVariableIds.has(v.id)) {
              return false;
            }
            seenVariableIds.add(v.id);
            return true;
          });

          // Ne retourner que si on a des variables à afficher
          if (filteredVariables.length === 0) {
            return null;
          }

          return {
            ...vInfos,
            variablesBoxes: filteredVariables
          };
        })
        .filter((item) => item !== null); // Filtrer les items null (groupements/variables déjà affichés)
    }

    // Mode template : comportement original
    return sortedAndFiltered.map((vInfos) => {
      return {
        ...vInfos,
        variablesBoxes: vInfos.variablesBoxes.filter((v) => !v.hideField)
      }
    });
    // Note: displayedVariables est un objet qui change de référence à chaque modification via immer
    // Pour optimiser, on pourrait utiliser Object.keys(displayedVariables).sort().join(',') comme dépendance
    // mais comme displayedVariables est utilisé dans le corps, il doit rester dans les dépendances
    // L'optimisation principale vient de la mémorisation de getVariablesGroupedByContainer dans LeftSide
  }, [variablesGroupedByContainer, variableOrderArray, sectionList, documentType, displayedVariables])

  const searchScrollToBoxId = useStore((state) => state.searchScrollToBoxId)
  const consumeScrollToBox = useStore((state) => state.consumeScrollToBox)
  const shouldScrollToVariableInFormViewer = useStore((state) => state.shouldScrollToVariableInFormViewer)
  const setShouldScrollToVariableInFormViewer = useStore((state) => state.setShouldScrollToVariableInFormViewer)

  useEffect(() => {
    if (searchScrollToBoxId) {
      scrollToItemInVariableEditor(searchScrollToBoxId, formItems)
      consumeScrollToBox()
    }
  }, [searchScrollToBoxId, consumeScrollToBox, formItems])

  useEffect(() => {
    // Ne scroller que si le flag indique qu'on doit le faire (clic depuis FormItemsArray)
    // Pas de scroll si on clique directement sur un container dans FormViewer
    if (shouldScrollToVariableInFormViewer && boxVariableSelectedId) {
      scrollToItemInVariableEditor(boxVariableSelectedId, formItems)
      setShouldScrollToVariableInFormViewer(false) // Réinitialiser le flag après le scroll
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxVariableSelectedId, shouldScrollToVariableInFormViewer])

  const renderVariable = useCallback((index) => {
    const formItem = formItems[index]
    const hasVariableSelected = boxVariableSelectedId && ((formItem.variableGroupBox?.id === boxVariableSelectedId) ||
      withAddressParts(formItem.variablesBoxes).some((b) => b.id === boxVariableSelectedId))

    return <FormItem
      hasVariableSelected={hasVariableSelected}
      formItem={formItem}
      zoom={zoom}
      formItems={formItems}
    />
  }, [boxVariableSelectedId, formItems, zoom])

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (isTemplate) {
        setZoom(100 * ((window.innerWidth - 32) / 100 * (factor - (8.5 * 100 / (window.innerWidth - 32)))) / ((landscape ? pageHeight : pageWidth) + 64));
      }
    });

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };

  }, [isTemplate, landscape, factor])

  const ref = useRef()

  return (
    <div style={{
      height: '100%',
      width: `100%`,
      paddingTop: contentPadding,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontSize: `${fontSize}px`,
      fontFamily: fontFamily,
      zoom: `${zoom}%`,
      backgroundColor: "#f0f2f5"
    }}
      ref={ref}
      data-role={pageBackground}
    >
      {!isTemplate &&
        <div style={{ width: landscape ? pageHeight : pageWidth, flexShrink: 0 }}>
          <ApplyAllPredefinedValues formItems={formItems} />
        </div>
      }
      <VList
        ref={listRef}
        style={{
          // flex 1 + minHeight 0 : la liste occupe la hauteur restante sous la
          // barre d'actions sans déborder du conteneur.
          flex: 1,
          minHeight: 0,
          backgroundColor: 'rgb(245, 245, 245)',
          width: landscape ? pageHeight : pageWidth,
          paddingTop: 16,
        }}
        className='no-scrollbar'
      >
        {formItems.map((formItem, index) => {
          const key = formItem.variableGroupBox
            ? `${formItem.containerId}-group-${formItem.variableGroupBox.id}`
            : `${formItem.containerId}-${formItem.variablesBoxes?.[0]?.id || 'unknown'}`;
          return (
            <div key={key}>
              {renderVariable(index)}
            </div>
          );
        })}
      </VList>
    </div>
  )
}

export default FormViewer
